<template>
  <BrandIcon class="logo" name="fullColour/appLogo" title="Dashboard" v-if="isSystem"></BrandIcon>
  <div class="userIdent" :title="identTitle" @click="handleClick" v-else-if="user || alt">
    <InitialIcon class="userInitial" :class="{ bordered } " :alt="alt" :color="colour" :icon="avatar" :large="large"
                 :toInitialise="toInitialise">
    </InitialIcon>
    <div v-if="displayNumber !== undefined" class="displayNumber">{{ displayNumber }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef } from 'vue';

import BrandIcon from '@/components/icons/BrandIcon.vue';
import InitialIcon from '@/components/InitialIcon.vue';
import { AppName, BrandColors } from '@/data/Branding';
import { LimitedUserDetails } from '@/data/datatypes/UserDetails';
import { convertUUIDtoNumber } from '@/data/helpers/InitialsHelper';

const guestDisplay: string = 'Guest';

interface Props {
  user?: LimitedUserDetails;
  alt?: string;
  altDisplayName?: string;
  title?: string;
  bordered?: boolean;
  large?: boolean;
  displayNumber?: number;
}
const props = withDefaults(defineProps<Props>(), {
  bordered: false,
});

interface Emits {
  (e: 'click', event: MouseEvent): void,
}
const emit = defineEmits<Emits>();

const colour: ComputedRef<string | null> = computed(() => {
  if (!props.user || (!props.user.color && !props.user.id)) {
    return null;
  }

  if (props.user.color) {
    return props.user.color;
  }

  // If the user doesn't have a colour set, then use one based on their ID
  const colorIndex = convertUUIDtoNumber(props.user.id) % BrandColors.DEFAULT_GENERATED_COLOURS.length;
  return BrandColors.DEFAULT_GENERATED_COLOURS[colorIndex];
});

const avatar: ComputedRef<string | null> = computed(() => {
  return props.user?.avatar ?? null;
});

const toInitialise: ComputedRef<string | undefined> = computed(() => {
  if (props.user && props.user.displayName) {
    return props.user.displayName;
  } else if (props.user && props.user.email) {
    return props.user.email;
  } else if (props.altDisplayName) {
    return props.altDisplayName;
  } else if (props.user && props.user.guest) {
    return guestDisplay;
  }
  return undefined;
});

const isSystem: ComputedRef<boolean> = computed(() => {
  return props.altDisplayName === AppName;
});

const identTitle: ComputedRef<string> = computed(() => {
  if (props.title) {
    return props.title;
  }
  if (!props.user) {
    return guestDisplay;
  }
  return props.user.displayName || props.user.email || guestDisplay;
});

function handleClick(event: MouseEvent): MouseEvent {
  emit('click', event);
  return event;
}
</script>

<style scoped lang="scss">
.userIdent {
  flex-grow: 0;
  user-select: none;
  position: relative;
}

.userInitial {
  vertical-align: middle;
  text-align: center;
  color: $colour-text-contrast;
  box-sizing: border-box;
  text-transform: capitalize;
  line-height: 2em;
  border-radius: $size-border-radius-primary;
  // Use em here so that the containing component can control the size.
  font-size: 1em;

  &.bordered {
    border: 2px solid $colour-background-light;
    border-radius: 50%;
  }
}

.onlineStatus {
  position: absolute;
  height: 0.5em;
  width: 0.5em;
  background-color: $colour-text-primary-light;
  border-radius: 50%;
  // These are very awkward values, but they roughly match the designs
  bottom: -0.0625em;
  right: -0.0625em;
  border: 2px solid $colour-border-contrast;

  &.userOnline {
    background-color: $colour-notification;
  }
}

.displayNumber {
  position: absolute;
  height: 1.75em;
  min-width: 1.75em;
  width: fit-content;
  font-size: 0.675em;
  font-weight: $weight-bold;
  color: $colour-text-contrast;
  background-color: $colour-background-dark;
  border: 1px solid $colour-border-contrast;
  border-radius: $size-border-radius-primary;
  bottom: -0.5em;
  right: -0.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  line-height: 1em;
}

.logo {
  height: 2em;
  width: 2em;
}
</style>
